/**
 * AI Image Editing API Client
 *
 * Client for editing images using text instructions.
 * Supports multiple providers: qwen-edit-2511, nano-banana-edit, seedream-edit, flux-kontext, etc.
 */

import { apiClient } from './apiClient';
import { getJobStatus, waitForJobCompletion } from './jobs';
import { unwrapEnvelope } from '../utils/httpUtils';
import sharedConstants from '../constants/shared_constants.json';

// The eight slugs POST /api/v1/image-editing/edit accepts, from
// constants/image/edit_providers.IMAGE_EDIT_PROVIDER_SLUGS. This union was
// missing 'qwen-edit-2511-lora' entirely -- the backend has served it all
// along and the UI could neither offer it nor even name it. The e2e test
// compares both directions now, so a slug added on either side fails loudly.
export type ImageEditProvider =
  | 'qwen-edit-2511'
  | 'qwen-edit-2511-lora'
  | 'qwen-edit'
  | 'nano-banana-edit'
  | 'nano-banana-pro-edit'
  | 'seedream-edit'
  | 'flux-kontext'
  | 'pruna-edit';

/** Editorial quality label -- a UI opinion, not a published API field. */
export type ImageEditQuality = 'good' | 'great' | 'excellent';

/** Editorial speed label -- a UI opinion, not a published API field. */
export type ImageEditSpeed = 'fast' | 'medium' | 'slow';

/**
 * One row of `GET /api/v1/image-editing/providers`, field-for-field.
 *
 * Names match the API (RULE #1.5); see `constants/pricing/image_editing.py
 * ::image_edit_provider_catalog`, which builds it.
 */
export interface ImageEditProviderApiInfo {
  id: ImageEditProvider;
  name: string;
  description: string;
  /**
   * USD the ACCOUNT pays for one edit at the backend's default size, markup
   * included -- not the vendor's cost. Varies with `size` when `pricedBySize`.
   */
  costPerEdit: number;
  /** The vendor's own endpoint slug, for support/debugging. */
  slug: string;
  /** 'flat' | 'per_megapixel' | 'resolution_tier' (EndpointPriceKind). */
  pricingModel: string;
  /** True when asking for a bigger output costs more than `costPerEdit`. */
  pricedBySize: boolean;
  /** e.g. "4096*4096"; null when the endpoint publishes no ceiling. */
  maxResolution: string | null;
  /** RESOLUTION_TIER editors only: account-paid USD per tier, e.g. {"4k": 0.36}. */
  tierCostPerEdit?: Record<string, number>;
}

/** The envelope's `data` for `GET /api/v1/image-editing/providers`. */
export interface ImageEditProvidersResponse {
  providers: ImageEditProviderApiInfo[];
  /** Whether THIS server can actually run an edit right now. */
  enabled: boolean;
  /** Backend's default provider slug -- the UI must not pick its own. */
  default: ImageEditProvider;
}

/** An API row plus the two editorial labels the API does not publish. */
export interface ImageEditProviderInfo extends ImageEditProviderApiInfo {
  /**
   * What one edit costs the account, in tokens, derived from `costPerEdit`.
   * There is no second opinion about this number anywhere in the frontend.
   */
  tokensPerEdit: number;
  quality?: ImageEditQuality;
  speed?: ImageEditSpeed;
}

// THE PRICE IS NOT HERE, AND MUST NEVER COME BACK.
//
// This file used to carry `tokensPerEdit` for eight providers and
// `getProviders()` returned that array without touching the network. Every
// number in it was stale on 2026-08-28: a user was shown 30 tokens and charged
// 57, and two providers the backend serves (qwen-edit, qwen-edit-2511-lora)
// were not listed at all. A test that parsed the numbers back out of this file
// only proved the copy existed; the copy was the defect (RULE #-2).
//
// `quality` and `speed` stay because the API publishes no such fields -- they
// are editorial labels about the models, not facts about an invoice. Anything
// the API DOES publish (name, description, price, tiers, max resolution) comes
// from the API, every time.
export const IMAGE_EDIT_PROVIDER_LABELS: Readonly<
  Record<ImageEditProvider, { quality: ImageEditQuality; speed: ImageEditSpeed }>
> = {
  'qwen-edit-2511': { quality: 'excellent', speed: 'medium' },
  'qwen-edit-2511-lora': { quality: 'great', speed: 'medium' },
  'qwen-edit': { quality: 'great', speed: 'medium' },
  'nano-banana-edit': { quality: 'great', speed: 'fast' },
  'nano-banana-pro-edit': { quality: 'excellent', speed: 'medium' },
  'seedream-edit': { quality: 'excellent', speed: 'medium' },
  'flux-kontext': { quality: 'great', speed: 'medium' },
  'pruna-edit': { quality: 'good', speed: 'fast' },
};

const TOKENS_PER_USD: number = sharedConstants.constants.misc.TOKENS_PER_USD;

/**
 * Convert a published account-paid USD price into the tokens the user is billed.
 *
 * Mirrors `constants/pricing/base.py::usd_to_tokens` -- `ceil(usd * TOKENS_PER_USD)`
 * -- reading the SAME `TOKENS_PER_USD` the backend exports, so the rate cannot
 * drift. It deliberately does NOT re-apply a markup: unlike the raw vendor
 * prices in `constants/videoSources.ts::usdToTokens`, `costPerEdit` already has
 * `IMAGE_EDIT_MARKUP_MULTIPLIER` baked in, and multiplying again would show 1.5x
 * the real bill.
 *
 * The backend also floors at `MINIMUM_TOKENS_PER_OPERATION` (= 1). That floor
 * cannot bind here: `ceil()` of any price above zero is already >= 1, and for a
 * genuinely free editor "0 tokens" is the honest display, not "1".
 */
export function imageEditTokensFromUsd(costPerEdit: number): number {
  return Math.ceil(costPerEdit * TOKENS_PER_USD);
}

/** Attach the editorial labels and the token price to one published row. */
function toProviderInfo(row: ImageEditProviderApiInfo): ImageEditProviderInfo {
  // A slug the backend serves but this build has no label for is still offered,
  // priced and selectable -- a missing adjective must never hide a provider the
  // way the old hardcoded array hid two of them. The e2e test fails loudly when
  // the two id sets diverge, which is where that belongs.
  const labels = IMAGE_EDIT_PROVIDER_LABELS[row.id] as
    | { quality: ImageEditQuality; speed: ImageEditSpeed }
    | undefined;
  return {
    ...row,
    tokensPerEdit: imageEditTokensFromUsd(row.costPerEdit),
    quality: labels?.quality,
    speed: labels?.speed,
  };
}

/** `GET /api/v1/image-editing/providers`, with prices resolved to tokens. */
export interface ImageEditProviderCatalog {
  providers: ImageEditProviderInfo[];
  enabled: boolean;
  default: ImageEditProvider;
}

/**
 * Output-size bounds shared by the size-honouring editors (Seedream documents
 * a 1024–4096 px range; Qwen / FLUX / Pruna accept the same "W*H" string).
 */
const EDIT_SIZE_MIN = 1024;
const EDIT_SIZE_MAX = 4096;

/**
 * Derive an aspect-ratio-preserving output size ("W*H") from the source image's
 * natural dimensions.
 *
 * Why: the editors that honour `size` (Qwen, Seedream, FLUX Kontext, Pruna)
 * otherwise fall back to a hard-coded square 1024*1024, which crops/distorts
 * non-square inputs. Matching the source aspect ratio is the intuitive default
 * for an *edit*. Editors that ignore `size` (Nano Banana) are unaffected.
 *
 * Nano Banana Pro uses a resolution tier (1k/2k/4k) rather than a size, and the
 * backend buckets this string on its long side. It is therefore NOT unaffected:
 * pass a long side of >=2048 to get 2k, >=4096 to get 4k.
 *
 * Returns undefined when dimensions are unknown so the backend default applies.
 */
export function deriveEditOutputSize(
  naturalWidth?: number,
  naturalHeight?: number
): string | undefined {
  if (!naturalWidth || !naturalHeight) return undefined;

  const shortSide = Math.min(naturalWidth, naturalHeight);
  const longSide = Math.max(naturalWidth, naturalHeight);

  let scale = 1;
  if (shortSide < EDIT_SIZE_MIN) scale = EDIT_SIZE_MIN / shortSide;
  if (longSide * scale > EDIT_SIZE_MAX) scale = EDIT_SIZE_MAX / longSide;

  const clamp = (v: number) =>
    Math.max(EDIT_SIZE_MIN, Math.min(EDIT_SIZE_MAX, Math.round((v * scale) / 32) * 32));

  return `${clamp(naturalWidth)}*${clamp(naturalHeight)}`;
}

export interface ImageEditRequest {
  /** Source image URL */
  image_url?: string;
  /** Source image as base64 */
  image_base64?: string;
  /** Text instruction describing the edit */
  instruction: string;
  /** Optional mask URL for inpainting */
  mask_url?: string;
  /** Provider to use */
  provider?: ImageEditProvider;
  /** Output size (e.g., "1024*1024") */
  size?: string;
  /** Output format */
  output_format?: 'png' | 'jpeg';
}

export interface ImageEditJob {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'error';
  progress?: number;
  message?: string;
  error?: string;
  result_url?: string;
  cost: number;
  estimated_tokens?: number;
}

export const imageEditingApi = {
  /**
   * Start image editing
   */
  async edit(request: ImageEditRequest): Promise<ImageEditJob> {
    const response = await apiClient.post('/api/v1/image-editing/edit', request);
    return unwrapEnvelope(response.data);
  },

  /**
   * Get job status
   */
  async getStatus(jobId: string): Promise<ImageEditJob> {
    return getJobStatus<ImageEditJob>(jobId);
  },

  /**
   * Poll until the job reaches a terminal status.
   */
  async waitForCompletion(
    jobId: string,
    onProgress?: (job: ImageEditJob) => void,
    pollIntervalMs = 3000
  ): Promise<ImageEditJob> {
    return waitForJobCompletion<ImageEditJob>(jobId, onProgress, pollIntervalMs);
  },

  /**
   * Ask the server which editors exist, what they cost and which is default.
   *
   * Rejects rather than returning a partial catalog: the caller must render
   * "price unknown", never a number this client invented (RULE #5).
   */
  async getProviders(): Promise<ImageEditProviderCatalog> {
    const response = await apiClient.get('/api/v1/image-editing/providers');
    const data = unwrapEnvelope<ImageEditProvidersResponse>(response.data);
    return {
      providers: data.providers.map(toProviderInfo),
      enabled: data.enabled,
      default: data.default,
    };
  },
};

export default imageEditingApi;
